import { Chip } from "@heroui/react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { K1PrimaryButton, K1SecondaryButton } from "@/components/ui/k1-button";
import { fmtArray, fmtText, parseArrayValue } from "../domain/formatters";
import { getIssueTypeLabel } from "../domain/status";
import type { EditTarget } from "../domain/types";

function normalizeToJsonArray(value: string): string {
  const items = parseArrayValue(value);
  if (items) return JSON.stringify(items);
  const trimmed = value.trim();
  if (!trimmed) return "[]";
  return JSON.stringify([trimmed]);
}

export function ReconciliationEditSheet({
  editing,
  isSaving,
  editValue,
  editComment,
  isFalsePositiveMode,
  trrRawValue,
  pdfRawValue,
  goldenIndicator,
  trrIndicator,
  pdfIndicator,
  setEditing,
  setEditValue,
  setEditComment,
  setIsFalsePositiveMode,
  onSave,
}: {
  editing: EditTarget | null;
  isSaving: boolean;
  editValue: string;
  editComment: string;
  isFalsePositiveMode: boolean;
  trrRawValue?: string;
  pdfRawValue?: string;
  goldenIndicator: {
    label: string;
    tone: "default" | "success" | "warning" | "danger";
  };
  trrIndicator: {
    label: string;
    tone: "default" | "success" | "warning" | "danger";
  };
  pdfIndicator: {
    label: string;
    tone: "default" | "success" | "warning" | "danger";
  };
  setEditing: (v: EditTarget | null) => void;
  setEditValue: (v: string) => void;
  setEditComment: (v: string) => void;
  setIsFalsePositiveMode: (v: boolean) => void;
  onSave: () => void;
}) {
  return (
    <Sheet
      open={Boolean(editing)}
      onOpenChange={(open) => {
        if (!open) setEditing(null);
      }}
    >
      <SheetContent
        hideOverlay
        className="w-[760px] sm:max-w-[760px] overflow-y-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="pr-10">
          <SheetTitle>Issue decision</SheetTitle>
          <SheetDescription>
            {editing
              ? `${editing.companyName} — ${editing.fieldLabel}`
              : "Select a field"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-8 space-y-7 pb-24">
          <div className="grid grid-cols-3 gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
            <div className="rounded-xl bg-white/95 px-4 py-3.5 ring-1 ring-slate-200">
              <p className="text-sm font-medium text-amber-700">Golden</p>
              <p className="mt-1.5 text-lg font-semibold leading-tight text-slate-800">
                {editing?.isArray ? fmtArray(editing?.goldenValue) : fmtText(editing?.goldenValue)}
              </p>
              {goldenIndicator.label ? (
                <div className="mt-2">
                  <Chip
                    size="sm"
                    variant="flat"
                    color={
                      goldenIndicator.tone === "success"
                        ? "success"
                        : goldenIndicator.tone === "warning"
                          ? "warning"
                          : goldenIndicator.tone === "danger"
                            ? "danger"
                            : "default"
                    }
                  >
                    {goldenIndicator.label}
                  </Chip>
                </div>
              ) : null}
            </div>
            <div className="rounded-xl bg-white/95 px-4 py-3.5 ring-1 ring-slate-200">
              <p className="text-sm font-medium text-emerald-700">TRR</p>
              <p className="mt-1.5 text-lg font-semibold leading-tight text-slate-800">
                {editing?.isArray ? fmtArray(trrRawValue) : fmtText(trrRawValue)}
              </p>
              {trrIndicator.label ? (
                <div className="mt-2">
                  <Chip
                    size="sm"
                    variant="flat"
                    color={
                      trrIndicator.tone === "success"
                        ? "success"
                        : trrIndicator.tone === "warning"
                          ? "warning"
                          : trrIndicator.tone === "danger"
                            ? "danger"
                            : "default"
                    }
                  >
                    {trrIndicator.label}
                  </Chip>
                </div>
              ) : null}
            </div>
            <div className="rounded-xl bg-white/95 px-4 py-3.5 ring-1 ring-slate-200">
              <p className="text-sm font-medium text-rose-700">PDF</p>
              <p className="mt-1.5 text-lg font-semibold leading-tight text-slate-800">
                {editing?.isArray ? fmtArray(pdfRawValue) : fmtText(pdfRawValue)}
              </p>
              {pdfIndicator.label ? (
                <div className="mt-2">
                  <Chip
                    size="sm"
                    variant="flat"
                    color={
                      pdfIndicator.tone === "success"
                        ? "success"
                        : pdfIndicator.tone === "warning"
                          ? "warning"
                          : pdfIndicator.tone === "danger"
                            ? "danger"
                            : "default"
                    }
                  >
                    {pdfIndicator.label}
                  </Chip>
                </div>
              ) : null}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Evidence
            </p>
            <div className="grid grid-cols-[96px_1fr] gap-y-1">
              <span className="font-semibold text-slate-600">Fund</span>
              <span>{editing?.fundName ?? "—"}</span>
              <span className="font-semibold text-slate-600">Issuer</span>
              <span>{editing?.companyName ?? "—"}</span>
              <span className="font-semibold text-slate-600">Field</span>
              <span>{editing?.fieldKey ?? "—"}</span>
              <span className="font-semibold text-slate-600">Business key</span>
              <span className="break-all">
                {editing?.control?.businessKey ?? "—"}
              </span>
              <span className="font-semibold text-slate-600">Issue type</span>
              <span>
                {getIssueTypeLabel(
                  editing?.control?.reasonCode,
                  editing?.control?.status,
                )}
              </span>
              <span className="font-semibold text-slate-600">Last seen</span>
              <span>
                {editing?.control?.lastSeenAt
                  ? new Date(editing.control.lastSeenAt).toLocaleString()
                  : "—"}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-normal text-slate-600">Choose source</p>
            <div className="grid grid-cols-4 gap-4">
              <button
                type="button"
                onClick={() => {
                  const raw = trrRawValue?.trim() || "";
                  const nextValue = editing?.isArray ? normalizeToJsonArray(raw) : raw;
                  setEditValue(nextValue);
                  setEditComment("source: xlsx");
                  setIsFalsePositiveMode(false);
                }}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-100"
              >
                <Chip
                  size="sm"
                  color="success"
                  variant="flat"
                  className="font-medium tracking-normal"
                >
                  XLSX
                </Chip>
                <p className="mt-2 text-sm font-normal text-slate-600">
                  Use TRR value
                </p>
              </button>
              <button
                type="button"
                onClick={() => {
                  const raw = pdfRawValue?.trim() || "";
                  const nextValue = editing?.isArray ? normalizeToJsonArray(raw) : raw;
                  setEditValue(nextValue);
                  setEditComment("source: pdf");
                  setIsFalsePositiveMode(false);
                }}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-100"
              >
                <Chip
                  size="sm"
                  color="danger"
                  variant="flat"
                  className="font-medium tracking-normal"
                >
                  PDF
                </Chip>
                <p className="mt-2 text-sm font-normal text-slate-600">
                  Use PDF value
                </p>
              </button>
              <button
                type="button"
                onClick={() => {
                  const raw = editing?.rawValue != null ? String(editing.rawValue) : "";
                  const nextValue = editing?.isArray ? normalizeToJsonArray(raw) : (editing?.formattedValue ?? "");
                  setEditValue(nextValue);
                  setEditComment("source: manual");
                  setIsFalsePositiveMode(false);
                }}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-100"
              >
                <Chip
                  size="sm"
                  color="secondary"
                  variant="flat"
                  className="font-medium tracking-normal"
                >
                  MANUAL
                </Chip>
                <p className="mt-2 text-sm font-normal text-slate-600">
                  Enter custom value
                </p>
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditComment("false positive");
                  setIsFalsePositiveMode(true);
                }}
                className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-100"
              >
                <Chip
                  size="sm"
                  color="warning"
                  variant="flat"
                  className="font-medium tracking-normal"
                >
                  FLAG
                </Chip>
                <p className="mt-2 text-sm font-normal text-slate-600">
                  False positive
                </p>
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">
              {editing?.isArray ? 'Selected value — format: ["val 1", "val 2"]' : "Selected value"}
            </label>
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder={editing?.isArray ? '["value 1", "value 2"]' : "Enter value"}
              className={
                editing?.isArray && editValue.trim() && !parseArrayValue(editValue)
                  ? "border-red-400 focus:border-red-500 focus:ring-red-100"
                  : ""
              }
            />
            {editing?.isArray && editValue.trim() && !parseArrayValue(editValue) && (
              <p className="text-xs text-red-500">
                Format invalide — saisir un JSON array valide : ["valeur 1", "valeur 2"]
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Comment</label>
            <textarea
              value={editComment}
              onChange={(e) => setEditComment(e.target.value)}
              placeholder="Optional reason"
              className="min-h-[90px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        <SheetFooter className="sticky bottom-0 left-0 right-0 z-10 mt-10 gap-3 bg-white/95 px-1 pt-4 pb-1 backdrop-blur-sm">
          <K1SecondaryButton
            onPress={() => setEditing(null)}
            isDisabled={isSaving}
          >
            Cancel
          </K1SecondaryButton>
          <K1PrimaryButton
            onPress={onSave}
            isDisabled={
              isSaving ||
              (isFalsePositiveMode ? !editComment.trim() : false) ||
              (editing?.isArray && editValue.trim() ? !parseArrayValue(editValue) : false)
            }
          >
            {isSaving ? "Saving..." : "Save"}
          </K1PrimaryButton>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
